import { Arrays, INVERSE_SQRT_2, panic, Unhandled, UUID } from "@opendaw/lib-std";
import { IconSymbol } from "@opendaw/studio-enums";
import { ArpeggioDeviceBox, AudioEffectCompositeBox, AudioEffectCompositeCellBox, AudioSinkDeviceBox, AutotuneDeviceBox, CompressorDeviceBox, ConvolverDeviceBox, CrusherDeviceBox, DattorroReverbDeviceBox, DelayDeviceBox, FoldDeviceBox, FrequencySplitBox, GateDeviceBox, GrooveShuffleBox, MaximizerDeviceBox, NeuralAmpDeviceBox, PitchDeviceBox, RevampDeviceBox, ReverbDeviceBox, SpielwerkDeviceBox, StereoCompositeBox, StereoToolDeviceBox, TidalDeviceBox, VelocityDeviceBox, VocoderDeviceBox, WaveshaperDeviceBox, WerkstattDeviceBox, ZeitgeistDeviceBox } from "@opendaw/studio-boxes";
import { InstrumentFactories } from "@opendaw/studio-adapters";
export var DeviceBoxes;
(function (DeviceBoxes) {
    const STEREO_ENTRY_COUNT = 2;
    const FREQUENCY_SPLIT_ENTRY_COUNT = 4;
    DeviceBoxes.MIDIEffectLabels = {
        Arpeggio: "Arpeggio", Pitch: "Pitch", Velocity: "Velocity", Zeitgeist: "Zeitgeist", Spielwerk: "Spielwerk"
    };
    DeviceBoxes.AudioEffectLabels = {
        Autotune: "Autotune", Compressor: "Compressor", Convolver: "Convolver", Crusher: "Crusher",
        DattorroReverb: "Dattorro Reverb", Delay: "Delay", Fold: "Fold", Gate: "Gate", Maximizer: "Maximizer",
        NeuralAmp: "Tone3000", Revamp: "Revamp", Reverb: "Reverb", StereoTool: "Stereo Tool", Tidal: "Tidal",
        Vocoder: "Vocoder", Waveshaper: "Waveshaper", Werkstatt: "Werkstatt", Composite: "FX Composite",
        StereoSplit: "Stereo Split", FrequencySplit: "Frequency Split", Sink: "Sink"
    };
    DeviceBoxes.midiEffectKeyOf = (boxName) => {
        switch (boxName) {
            case "ArpeggioDeviceBox": return "Arpeggio";
            case "PitchDeviceBox": return "Pitch";
            case "VelocityDeviceBox": return "Velocity";
            case "ZeitgeistDeviceBox": return "Zeitgeist";
            case "SpielwerkDeviceBox": return "Spielwerk";
            default: return panic(`Unknown midi-effect box '${boxName}'`);
        }
    };
    DeviceBoxes.audioEffectKeyOf = (boxName) => {
        switch (boxName) {
            case "AutotuneDeviceBox": return "Autotune";
            case "CompressorDeviceBox": return "Compressor";
            case "ConvolverDeviceBox": return "Convolver";
            case "CrusherDeviceBox": return "Crusher";
            case "DattorroReverbDeviceBox": return "DattorroReverb";
            case "DelayDeviceBox": return "Delay";
            case "FoldDeviceBox": return "Fold";
            case "GateDeviceBox": return "Gate";
            case "MaximizerDeviceBox": return "Maximizer";
            case "NeuralAmpDeviceBox": return "NeuralAmp";
            case "RevampDeviceBox": return "Revamp";
            case "ReverbDeviceBox": return "Reverb";
            case "StereoToolDeviceBox": return "StereoTool";
            case "TidalDeviceBox": return "Tidal";
            case "VocoderDeviceBox": return "Vocoder";
            case "WaveshaperDeviceBox": return "Waveshaper";
            case "WerkstattDeviceBox": return "Werkstatt";
            case "AudioEffectCompositeBox": return "Composite";
            case "StereoCompositeBox": return "StereoSplit";
            case "FrequencySplitBox": return "FrequencySplit";
            case "AudioSinkDeviceBox": return "Sink";
            default: return panic(`Unknown audio-effect box '${boxName}'`);
        }
    };
    DeviceBoxes.instrumentKeyOf = (boxName) => {
        switch (boxName) {
            case "VaporisateurDeviceBox": return "Vaporisateur";
            case "PlayfieldDeviceBox": return "Playfield";
            case "NanoDeviceBox": return "Nano";
            case "SoundfontDeviceBox": return "Soundfont";
            case "MIDIOutputDeviceBox": return "MIDIOutput";
            case "TapeDeviceBox": return "Tape";
            case "NeonDeviceBox": return "Neon";
            case "TubularDeviceBox": return "Tubular";
            case "CubedDeviceBox": return "Cubed";
            case "ApparatDeviceBox": return "Apparat";
            case "InstrumentCompositeBox": return "InstrumentComposite";
            default: return panic(`Unknown instrument box '${boxName}'`);
        }
    };
    DeviceBoxes.isMIDIEffectBox = (boxName) => ["ArpeggioDeviceBox", "PitchDeviceBox", "VelocityDeviceBox", "ZeitgeistDeviceBox", "SpielwerkDeviceBox"]
        .includes(boxName);
    DeviceBoxes.isAudioEffectBox = (boxName) => ["AutotuneDeviceBox", "CompressorDeviceBox", "ConvolverDeviceBox", "CrusherDeviceBox",
        "DattorroReverbDeviceBox", "DelayDeviceBox", "FoldDeviceBox", "GateDeviceBox", "MaximizerDeviceBox",
        "NeuralAmpDeviceBox", "RevampDeviceBox", "ReverbDeviceBox", "StereoToolDeviceBox", "TidalDeviceBox",
        "VocoderDeviceBox", "WaveshaperDeviceBox", "WerkstattDeviceBox", "AudioEffectCompositeBox",
        "StereoCompositeBox", "FrequencySplitBox", "AudioSinkDeviceBox"].includes(boxName);
    DeviceBoxes.isInstrumentBox = (boxName) => ["VaporisateurDeviceBox", "PlayfieldDeviceBox", "NanoDeviceBox", "SoundfontDeviceBox",
        "MIDIOutputDeviceBox", "TapeDeviceBox", "NeonDeviceBox", "TubularDeviceBox", "CubedDeviceBox", "ApparatDeviceBox",
        "InstrumentCompositeBox"]
        .includes(boxName);
    DeviceBoxes.createInstrument = (boxGraph, key, host, label) => {
        const factory = InstrumentFactories.Named[key];
        return factory.create(boxGraph, host, label, factory.defaultIcon);
    };
    DeviceBoxes.createMIDIEffect = (boxGraph, key, host, index) => {
        const label = DeviceBoxes.MIDIEffectLabels[key];
        switch (key) {
            case "Arpeggio":
                return ArpeggioDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "Pitch":
                return PitchDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "Velocity":
                return VelocityDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "Zeitgeist": {
                const shuffleBox = GrooveShuffleBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue("Shuffle");
                    box.duration.setValue(480);
                });
                return ZeitgeistDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.groove.refer(shuffleBox);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            }
            case "Spielwerk":
                return SpielwerkDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            default:
                return Unhandled(key);
        }
    };
    DeviceBoxes.createAudioEffect = (boxGraph, key, host, index) => {
        const label = DeviceBoxes.AudioEffectLabels[key];
        switch (key) {
            case "Autotune":
                return AutotuneDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "Compressor":
                return CompressorDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "Convolver":
                return ConvolverDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "Crusher":
                return CrusherDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "DattorroReverb":
                return DattorroReverbDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "Delay":
                return DelayDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                    box.version.setValue(1);
                });
            case "Fold":
                return FoldDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "Gate":
                return GateDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "Maximizer":
                return MaximizerDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "NeuralAmp":
                return NeuralAmpDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "Revamp":
                return RevampDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.highPass.frequency.setInitValue(40.0);
                    box.highPass.order.setInitValue(1);
                    box.highPass.q.setInitValue(Math.SQRT1_2);
                    box.highPass.enabled.setInitValue(true);
                    box.lowShelf.frequency.setInitValue(80.0);
                    box.lowShelf.gain.setInitValue(6);
                    box.lowBell.frequency.setInitValue(120.0);
                    box.lowBell.gain.setInitValue(6);
                    box.lowBell.q.setInitValue(INVERSE_SQRT_2);
                    box.midBell.frequency.setInitValue(640.0);
                    box.midBell.q.setInitValue(INVERSE_SQRT_2);
                    box.midBell.gain.setInitValue(6);
                    box.highBell.frequency.setInitValue(3600.0);
                    box.highBell.q.setInitValue(INVERSE_SQRT_2);
                    box.highBell.gain.setInitValue(6);
                    box.highShelf.frequency.setInitValue(10000.0);
                    box.highShelf.gain.setInitValue(6);
                    box.lowPass.frequency.setInitValue(15000.0);
                    box.lowPass.order.setInitValue(1);
                    box.lowPass.q.setInitValue(Math.SQRT1_2);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "Reverb":
                return ReverbDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.preDelay.setInitValue(0.001);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "StereoTool":
                return StereoToolDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "Tidal":
                return TidalDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.depth.setValue(0.75);
                    box.host.refer(host);
                });
            case "Vocoder":
                return VocoderDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "Waveshaper":
                return WaveshaperDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "Sink":
                return AudioSinkDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "Werkstatt":
                return WerkstattDeviceBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "Composite":
                return AudioEffectCompositeBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
            case "StereoSplit": {
                const composite = StereoCompositeBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
                Arrays.create(entryIndex => DeviceBoxes.createCompositeEntry(boxGraph, composite.entries, entryIndex), STEREO_ENTRY_COUNT);
                return composite;
            }
            case "FrequencySplit": {
                const composite = FrequencySplitBox.create(boxGraph, UUID.generate(), box => {
                    box.label.setValue(label);
                    box.index.setValue(index);
                    box.host.refer(host);
                });
                Arrays.create(entryIndex => DeviceBoxes.createCompositeEntry(boxGraph, composite.entries, entryIndex), FREQUENCY_SPLIT_ENTRY_COUNT);
                return composite;
            }
            default:
                return Unhandled(key);
        }
    };
    DeviceBoxes.createCompositeEntry = (boxGraph, entries, index) => AudioEffectCompositeCellBox.create(boxGraph, UUID.generate(), box => {
        box.composite.refer(entries);
        box.index.setValue(index);
    });
    DeviceBoxes.iconName = (symbol) => IconSymbol.toName(symbol);
})(DeviceBoxes || (DeviceBoxes = {}));
